import { makeStyles } from 'vendor/material';
import { fontWeightMedium } from 'utils/fontWeightConstants';

const CHOICES_BREAKPOINT = 1350;

const useStyles = makeStyles((theme) => ({
  alertCallout: {
    backgroundColor: theme.palette.YELLOW050,
    padding: theme.spacing(2.5),
  },
  alertHeaderContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  alertHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  alertHeaderText: {
    paddingLeft: theme.spacing(1),
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  divider: {
    margin: theme.spacing(4, 0),
  },
  pageTitle: {
    fontSize: theme.typography.pxToRem(20),
    lineHeight: theme.typography.pxToRem(24),
    fontWeight: fontWeightMedium,
    color: theme.palette.NEUTRAL900,
    marginBottom: theme.spacing(1),
  },
  pageSubtitle: {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: theme.typography.pxToRem(20),
    color: theme.palette.NEUTRAL300,
    '& a': {
      color: theme.palette.BLUE300,
    },
  },
  pageSecondaryTitle: {
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(20),
    fontWeight: fontWeightMedium,
    color: theme.palette.NEUTRAL200,
    marginBottom: theme.spacing(2),
  },
  choiceCloud: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    [theme.breakpoints.down(CHOICES_BREAKPOINT)]: {
      flexDirection: 'column',
    },
    '& > div': {
      flex: '1 0 auto',
      [theme.breakpoints.down(CHOICES_BREAKPOINT)]: {
        width: '100%',
        margin: theme.spacing(1, 0),
      },
      '&:not(:last-child)': {
        marginRight: theme.spacing(2),
      },
    },
  },
  generalForm: {
    marginBottom: theme.spacing(3),
  },
  providerForm: {
    marginBottom: theme.spacing(10),
  },
  banner: {
    marginBottom: theme.spacing(3),
    '& li': {
      '&:not(:last-child)': {
        paddingBottom: theme.spacing(2),
      },
    },
  },
  bannerContent: {
    color: theme.palette.NEUTRAL500,
  },
  link: {
    color: theme.palette.BLUE400,
  },
  noHoverBackground: {
    '&:hover, &:focus': {
      backgroundColor: `${theme.palette.NEUTRAL000} !important`,
      boxShadow: 'none !important',
    },
  },
  chkBoxFormControl: {
    margin: 'unset', // in this case we don't want to negative margin
  },
  checkBox: {
    marginLeft: '-4px',
  },
  checkBoxGcp: {
    marginLeft: theme.spacing(-3.5),
  },
  checkboxLabel: {
    color: theme.palette.NEUTRAL800,
  },
  checkboxLabelGcp: {
    marginLeft: 0,
  },
  textGrid: {
    marginTop: '2px',
    marginBottom: theme.spacing(1),
  },
  textGridGcp: {
    marginTop: '2px',
    marginBottom: theme.spacing(1),
  },
  ol: {
    paddingLeft: theme.spacing(3),
    marginTop: theme.spacing(1),
    '& li': {
      '&:not(:last-child)': {
        paddingBottom: theme.spacing(1),
      },
    },
  },
  itemWrapper: {
    flexWrap: 'nowrap',
  },
  interGrid: {
    '& > div': {
      marginBottom: theme.spacing(1),
    },
  },
  directions: {
    backgroundColor: theme.palette.NEUTRAL020,
    color: theme.palette.NEUTRAL600,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
    fontSize: theme.typography.pxToRem(16),
    '& span': {
      color: theme.palette.NEUTRAL200,
    },
  },
  marginTopBump: {
    marginTop: theme.spacing(1),
  },
  infoTitle: {
    color: theme.palette.NEUTRAL300,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: fontWeightMedium,
  },
  infoBody: {
    color: theme.palette.NEUTRAL500,
    fontSize: theme.typography.pxToRem(14),
  },
  listBackground: {
    backgroundColor: theme.palette.NEUTRAL010,
    padding: theme.spacing(1),
  },
  preLine: {
    whiteSpace: 'pre-line',
  },
  listStyleTypeAsDiscs: {
    listStyleType: 'disc',
  },
  listStyleTypeAsLowerAlpha: {
    listStyleType: 'lower-alpha',
  },
  unorderedListSpacing: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  beforeYouBeginList: {
    paddingLeft: theme.spacing(3),
  },
  buttonText: {
    // Necessary specific style to target button labels, this overrides the default uppercase text transform in MUI
    '& > span': {
      textTransform: 'none',
    },
  },
  iconButton: {
    color: theme.palette.NEUTRAL700,
    '&.Mui-disabled': {
      color: theme.palette.NEUTRAL100,
    },
  },
  listText: {
    color: theme.palette.NEUTRAL600,
    whiteSpace: 'pre-line',
  },
  stack: {
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
  },
  disabledStack: {
    cursor: 'not-allowed',
    opacity: '50%',
  },
  note: {
    color: theme.palette.NEUTRAL600,
    fontStyle: 'italic',
    fontSize: '14px',
  },
  text: {
    color: theme.palette.NEUTRAL600,
    whiteSpace: 'pre-line',
  },
  requiredLabelAsterisk: {
    color: theme.palette.RED400,
  },
  configurationKeys: {
    color: theme.palette.NEUTRAL700,
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.pxToRem(14),
    display: 'flex',
    flexDirection: 'column',
  },
  radioGroup: {
    marginLeft: theme.spacing(0.5),
  },
  disabled: {
    color: theme.palette.NEUTRAL100,
  },
  checkboxParentContainer: {
    display: 'flex',
    paddingBottom: theme.spacing(2),
    '& > label': {
      alignItems: 'flex-start',
    },
  },
  contactsFormArrayContainer: {
    marginLeft: theme.spacing(4),
  },
  awsPartnerSteps: {
    fontWeight: 400,
    fontSize: theme.spacing(2),
    lineHeight: 2,
  },
  awsPartnerStepsEmphasis: {
    fontWeight: 700,
  },
  awsSolutionsFormToggle: {
    fontWeight: 700,
    fontSize: theme.spacing(2),
    lineHeight: 3,
  },
  solutionsSubHeader: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
  },
  solutionsSubHeaderText: {
    fontWeight: 700,
    fontSize: theme.spacing(2),
    lineHeight: 3,
  },
  solutionsBulkUploadButton: {
    backgroundColor: theme.palette.NEUTRAL000,
  },
  checkboxLabelForSolution: {
    marginRight: theme.spacing(0),
    marginBottom: theme.spacing(3),
    '& label': {
      marginRight: theme.spacing(0),
      '& > span:last-child': {
        display: 'none',
      },
    },
  },
  iconButtonMatcher: {
    marginBottom: theme.spacing(2),
  },
}));

export default useStyles;
